import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '../utils/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium whitespace-nowrap select-none',
    'rounded-full transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&>svg]:size-4 [&>svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-brand-600 text-white shadow-[0_8px_20px_-6px_rgb(91_33_200/0.55)]',
          'hover:bg-brand-700 hover:shadow-[0_12px_28px_-8px_rgb(91_33_200/0.65)]',
          'active:translate-y-px active:shadow-[0_4px_12px_-4px_rgb(91_33_200/0.45)]',
        ],
        accent: [
          'bg-accent-400 text-brand-900 shadow-[0_8px_20px_-6px_rgb(245_152_0/0.55)]',
          'hover:bg-accent-300 hover:shadow-[0_12px_28px_-8px_rgb(245_152_0/0.65)]',
          'active:translate-y-px',
        ],
        secondary: [
          'bg-brand-50 text-brand-700 border border-brand-100',
          'hover:bg-brand-100 hover:border-brand-200',
        ],
        outline: [
          'bg-transparent text-foreground border border-[var(--color-border-strong)]',
          'hover:bg-[var(--color-surface-subtle)] hover:border-brand-300',
        ],
        ghost: [
          'bg-transparent text-foreground',
          'hover:bg-[var(--color-surface-subtle)]',
        ],
        link: [
          'bg-transparent text-brand-600 underline-offset-4',
          'hover:underline hover:text-brand-700',
        ],
        danger: [
          'bg-red-600 text-white shadow-[0_8px_20px_-6px_rgb(220_38_38/0.45)]',
          'hover:bg-red-700',
        ],
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-sm',
        lg: 'h-13 px-8 text-base',
        icon: 'size-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading, leftIcon, rightIcon, children, disabled, ...props },
    ref,
  ) => {
    // Radix Slot kizárólag EGY gyermeket fogad → asChild esetén az ikonokat
    // a child-elem belsejébe injektáljuk (cloneElement), nem testvérként.
    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{ children?: React.ReactNode }>;
      const cloned = React.cloneElement(
        child,
        undefined,
        <>
          {loading ? <Loader2 className="animate-spin" /> : leftIcon}
          {child.props.children}
          {!loading && rightIcon}
        </>,
      );
      return (
        <Slot
          ref={ref as React.Ref<HTMLElement>}
          className={cn(buttonVariants({ variant, size, className }))}
          aria-busy={loading || undefined}
          {...props}
        >
          {cloned}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled ?? loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
